import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Button } from "@mui/material";
import AudioFileUpload from "../ManageKaraoke/AudioFileUpload";
import AudioPlayer from "../../components/UploadAudio/AudioPlayer";
import useStyles from "./DanceStyles";
import DanceForm from "./DanceForm";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import { useDispatch } from "react-redux";
import { setLoading, showToast } from "../../store/slices/common";
import { createDance, updateDance } from "../../http/dance";
import { appendLibrarySticker } from "../../components/ThemeStickerPicker";
import {
  createStickerUploadHandler,
  rememberUploadedSticker,
  syncStickerListRef,
} from "../../utils/stickerUploads";
import {
  isValidMediaId,
  MAX_STICKERS,
  normalizeStickers,
} from "../ManageChallenge/helpers";
import routePaths from "../../router/route-paths";
import { useNavigate } from "react-router-dom";
import { danceFormatFromPlayModes, playModesFromDanceFormat, DANCE_FORMATS } from "./danceHelpers";
import { normalizeFeedGroupSize } from "../../components/FormElements/FeedGroupSizeField";
import useThemeContext, { themeToContext } from "../../hooks/useThemeContext";
import { listOpenThemes } from "../../http/themes";

const hasThemeParam = () =>
  new URLSearchParams(window.location.search).has("themeId");

const UploadDanceFile = () => {
  // Opened from the theme form (new tab, ?themeId=): a create, never an edit,
  // so a stale editDance entry from another tab must not turn it into one.
  const fromTheme = useRef(hasThemeParam()).current;
  if (fromTheme) {
    try {
      localStorage.removeItem("editDance");
    } catch {
      // ignore
    }
  }
  const { themeContext: linkedThemeContext, loading: themeLoading, error: themeError } =
    useThemeContext({ enabled: fromTheme, maxStickers: MAX_STICKERS });
  // Theme chosen from the picker inside the form (plain "Add Dance" flow,
  // no ?themeId=). Behaves exactly like the deep-link context: dates lock to
  // the theme, its stickers seed the list, and the track is linked on save.
  const [pickedTheme, setPickedTheme] = useState(null);
  // Active + upcoming themes for that picker; null until loaded (or when the
  // picker does not apply: edit, or opened from a theme).
  const [themeOptions, setThemeOptions] = useState(null);
  const themeContext = linkedThemeContext || pickedTheme;
  const classes = useStyles();
  const [audio, setAudio] = useState(null);
  const [audioId, setAudioId] = useState("");
  const [duration, setDuration] = useState(0);
  const [isEnabled, setIsEnabled] = useState(false);
  const [danceForm, setDanceForm] = useState({
    title: "",
    coverImageId: "",
    image: "",
    availabilityStartTime: null,
    availabilityEndTime: null,
    isUnscheduled: false,
    ladder: "ranked",
    ladderLocked: false,
    format: DANCE_FORMATS.LIVE,
    playModes: playModesFromDanceFormat(DANCE_FORMATS.LIVE),
    feedGroupSize: null,
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [edit, setEdit] = useState("");
  // Sticker images shown on the mobile recording edit screen for this track.
  // Same {mediaId, url} shape and replace-as-a-whole-set semantics as
  // challenge stickers (ManageChallenge).
  const [stickers, setStickers] = useState([]);
  const [stickersDirty, setStickersDirty] = useState(false);
  const stickersRef = useRef([]);
  syncStickerListRef(stickersRef, stickers);
  const editIdRef = useRef("");
  const audioRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  editIdRef.current = edit;
  const stickerUploadRef = useRef(null);
  if (!stickerUploadRef.current) {
    stickerUploadRef.current = createStickerUploadHandler({
      max: MAX_STICKERS,
      getCurrent: () => stickersRef.current.length,
      folder: "dance-stickers",
      metadata: () => ({
        type: "sticker",
        ...(editIdRef.current && { danceId: editIdRef.current }),
      }),
      onUploaded: (media) => {
        setStickers(rememberUploadedSticker(stickersRef, media, MAX_STICKERS));
        setStickersDirty(true);
      },
      dispatch,
      setLoading: (value) => dispatch(setLoading(value)),
    });
  }

  // Deep-link theme context arrives async; seed the form with its dates and
  // stickers before the metadata form mounts (it only renders after the
  // audio upload). A theme chosen in the picker is applied by handlePickTheme.
  useEffect(() => {
    if (!linkedThemeContext) return;
    setDanceForm((prev) => ({
      ...prev,
      availabilityStartTime: linkedThemeContext.startDate,
      availabilityEndTime: linkedThemeContext.endDate,
      isUnscheduled: false,
    }));
    setStickers(linkedThemeContext.stickers);
  }, [linkedThemeContext]);

  useEffect(() => {
    if (!themeError) return;
    dispatch(showToast({ color: "warning", msg: themeError }));
  }, [themeError, dispatch]);

  // Themes the track can still be added to, for the in-form picker. Only on a
  // plain create: edits keep their saved theme, and a deep link already
  // fixed the theme.
  useEffect(() => {
    if (fromTheme) return undefined;
    try {
      if (localStorage.getItem("editDance")) return undefined;
    } catch {
      // ignore
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await listOpenThemes();
        if (!cancelled) setThemeOptions(list);
      } catch (error) {
        console.error("Error loading themes for the dance form:", error);
        if (!cancelled) {
          dispatch(
            showToast({
              color: "warning",
              msg: "Could not load themes. Set the dates manually.",
            })
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fromTheme, dispatch]);

  // Picker change: swap the previous theme's stickers for the new theme's
  // (keeping anything the admin uploaded) and fill the dates. The form
  // locks its date fields to the theme window from themeContext.
  const handlePickTheme = (themeId) => {
    const theme = themeOptions?.find((option) => String(option._id) === themeId);
    const next = theme ? themeToContext(theme, MAX_STICKERS) : null;
    const previousIds = new Set(
      (pickedTheme?.stickers || []).map((sticker) => sticker.mediaId)
    );
    setPickedTheme(next);
    setDanceForm((prev) => ({
      ...prev,
      availabilityStartTime: next?.startDate ?? prev.availabilityStartTime,
      availabilityEndTime: next?.endDate ?? prev.availabilityEndTime,
      isUnscheduled: next ? false : prev.isUnscheduled,
    }));
    setStickers((prev) => {
      const kept = prev.filter((sticker) => !previousIds.has(sticker.mediaId));
      return (next?.stickers || []).reduce(
        (list, sticker) => appendLibrarySticker(list, sticker, MAX_STICKERS),
        kept
      );
    });
  };

  useEffect(() => {
    const dance = fromTheme ? null : JSON.parse(localStorage.getItem("editDance"));
    if (dance) {
      setAudio(dance.audio);
      audioRef.current = new Audio(dance.audio);
      // Set audioId if available in the dance object
      if (dance.audioId) {
        setAudioId(dance.audioId);
      }
      // Also set duration if available
      if (dance.duration) {
        setDuration(dance.duration);
      }
      setDanceForm({
        title: dance?.title,
        image: dance.coverImage,
        coverImageId: dance.coverImageId || "",
        availabilityStartTime: dance?.availabilityStartTime || null,
        availabilityEndTime: dance?.availabilityEndTime || null,
        isUnscheduled: Boolean(dance?.isUnscheduled),
        ladder: dance?.ladder === "for_fun" ? "for_fun" : "ranked",
        ladderLocked: dance?.ladderLocked === true,
        format: danceFormatFromPlayModes(dance?.playModes),
        playModes: playModesFromDanceFormat(danceFormatFromPlayModes(dance?.playModes)),
        feedGroupSize: normalizeFeedGroupSize(dance?.feedGroupSize),
      });
      // Saved stickers are bare URL strings; normalize to {mediaId, url}
      setStickers(normalizeStickers(dance));
      setEdit(dance?._id);
    }
  }, []);

  const handleAudioUpload = (file) => {
    const fileUrl = URL.createObjectURL(file);
    audioRef.current = new Audio(fileUrl);
    setAudio(fileUrl);
  };

  const removeAudio = () => {
    setAudio(null);
  };

  const pauseAudio = () => {
    setIsPlaying(false);
    audioRef.current.pause();
  };

  const handleStickerUpload = stickerUploadRef.current;

  const handleAddLibrarySticker = (sticker) => {
    setStickers((prev) => appendLibrarySticker(prev, sticker, MAX_STICKERS));
    setStickersDirty(true);
  };

  const handleRemoveSticker = (index) => {
    setStickers((prev) => prev.filter((_, i) => i !== index));
    setStickersDirty(true);
  };

  const handleClearStickers = () => {
    setStickers([]);
    setStickersDirty(true);
  };

  // Helper to check if a value is valid (not null, undefined, or empty string)
  const isValidValue = (value) => {
    return value !== null && value !== undefined && value !== '';
  };

  const createDanceHandler = async () => {
    // Validate required fields
    if (!danceForm.title || !danceForm.title.trim()) {
      alert('Title is required');
      return;
    }
    // For create mode, audioId is required. For edit mode, it's optional if audio already exists
    if (!edit && (!audioId || (typeof audioId === 'string' && !audioId.trim()))) {
      alert('Audio file is required');
      return;
    }
    
    // Build payload, only including fields with valid values
    const payload = {
      title: danceForm.title.trim(),
      audioId: typeof audioId === 'string' ? audioId.trim() : audioId,
      duration: duration || 0,
    };
    
    // Only include coverImageId if it has a valid value
    if (isValidValue(danceForm.coverImageId)) {
      payload.coverImageId = danceForm.coverImageId;
    }
    
    // Include availability time fields if set
    if (isValidValue(danceForm.availabilityStartTime)) {
      payload.availabilityStartTime = danceForm.availabilityStartTime;
    }
    if (isValidValue(danceForm.availabilityEndTime)) {
      payload.availabilityEndTime = danceForm.availabilityEndTime;
    }
    if (danceForm.isUnscheduled) {
      payload.isUnscheduled = true;
    }
    // Default ladder shown when the track is opened. Players can switch.
    // Live-only tracks are ranked.
    const createFormat = danceForm.format || DANCE_FORMATS.LIVE;
    payload.playModes = playModesFromDanceFormat(createFormat);
    payload.ladder =
      createFormat === DANCE_FORMATS.LIVE_ONLY
        ? "ranked"
        : danceForm.ladder === "for_fun"
          ? "for_fun"
          : "ranked";
    // Hard-set (no player choice). Live-only tracks are ranked anyway.
    payload.ladderLocked =
      createFormat === DANCE_FORMATS.LIVE_ONLY || danceForm.ladderLocked === true;
    payload.feedGroupSize = danceForm.feedGroupSize ?? null;

    // Stickers on create: send the Media ids of the uploaded set. Under a
    // theme the list is always sent, so an empty one means "none" rather than
    // "inherit the theme's library".
    const createStickerIds = stickers
      .map((sticker) => sticker.mediaId)
      .filter(isValidMediaId);
    if (createStickerIds.length || themeContext?.themeId) {
      payload.stickerIds = createStickerIds;
    }
    if (!edit && themeContext?.themeId) {
      // Server links the track to the theme in the same request
      payload.themeId = themeContext.themeId;
    }

    // Build edit payload
    let editPayload = {
      title: danceForm.title.trim(),
      duration: duration || 0,
    };
    
    // Only include audioId if it has a valid value
    if (isValidValue(audioId)) {
      editPayload.audioId = audioId;
    }
    
    // Only include coverImageId if it has a valid value
    if (isValidValue(danceForm.coverImageId)) {
      editPayload.coverImageId = danceForm.coverImageId;
    }
    
    // Include availability time fields if set
    if (danceForm.isUnscheduled) {
      // Date TBD: clear any previously saved window along with setting the flag
      editPayload.availabilityStartTime = null;
      editPayload.availabilityEndTime = null;
    } else {
      if (isValidValue(danceForm.availabilityStartTime)) {
        editPayload.availabilityStartTime = danceForm.availabilityStartTime;
      }
      if (isValidValue(danceForm.availabilityEndTime)) {
        editPayload.availabilityEndTime = danceForm.availabilityEndTime;
      }
    }
    editPayload.isUnscheduled = Boolean(danceForm.isUnscheduled);
    const editFormat = danceForm.format || DANCE_FORMATS.LIVE;
    editPayload.playModes = playModesFromDanceFormat(editFormat);
    editPayload.ladder =
      editFormat === DANCE_FORMATS.LIVE_ONLY
        ? "ranked"
        : danceForm.ladder === "for_fun"
          ? "for_fun"
          : "ranked";
    editPayload.ladderLocked =
      editFormat === DANCE_FORMATS.LIVE_ONLY || danceForm.ladderLocked === true;
    editPayload.feedGroupSize = danceForm.feedGroupSize ?? null;

    // Stickers are replaced as a whole set, so only send them when touched.
    // Pre-existing stickers only have URLs (no Media ids), so keeping some
    // while changing others isn't possible — same rule as challenges.
    if (edit && stickersDirty) {
      const stickerIds = stickers.map((sticker) => sticker.mediaId);
      if (stickerIds.some((id) => !isValidMediaId(id))) {
        dispatch(
          showToast({
            color: "error",
            msg: "Some stickers can't be kept automatically. Remove all stickers and re-upload the ones you want.",
          })
        );
        return;
      }
      // Empty array clears the stickers on the server
      editPayload.stickerIds = stickerIds;
    }

    try {
      dispatch(setLoading(true));
      edit
        ? await updateDance(editPayload, edit)
        : await createDance(payload);
      if (!edit && themeContext?.themeId) {
        dispatch(
          showToast({
            color: "success",
            msg: `Dance added to theme ${themeContext.title || ""}`.trim() + ".",
          })
        );
      }
      navigate(routePaths.MANAGE_DANCE);
    } catch (err) {
      console.error('Error creating dance:', err);
      console.error('Error response:', err?.response?.data);
      
      // Show error message to user
      const errorMessage = err?.response?.data?.message || err?.message || 'Unknown error occurred';
      alert(`Error creating dance track: ${errorMessage}\n\nCheck console for details.`);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const goBack = () => {
    navigate(routePaths.MANAGE_DANCE);
  };

  if (themeLoading) {
    return <Box className={classes.root} />;
  }

  return (
    <Box className={classes.root}>
      {/* Step 1: if no audio file show option to upload a file */}
      {!audio && (
        <AudioFileUpload
          setAudio={handleAudioUpload}
          setAudioId={setAudioId}
          setDuration={setDuration}
        />
      )}

      {/* Once audio is uploaded, show audio player and form */}
      {audio && (
        <>
          <Typography
            className={classes.commandsTitle}
            sx={{ margin: "20px" }}
          >
            {danceForm?.title || "Dance Track"}
          </Typography>
          <AudioPlayer
            audioRef={audioRef}
            sync={true}
            lyrics={false}
            onIconClick={removeAudio}
            setIsPlayingFromSync={setIsPlaying}
            isPlayingFromSync={isPlaying}
          />
          
          {/* Dance Form - title, cover image, stickers, availability */}
          <DanceForm
            setIsEnabled={setIsEnabled}
            setDanceForm={setDanceForm}
            danceForm={danceForm}
            stickers={stickers}
            onStickerUpload={handleStickerUpload}
            onAddLibrarySticker={handleAddLibrarySticker}
            onRemoveSticker={handleRemoveSticker}
            onClearStickers={handleClearStickers}
            themeContext={themeContext}
            themeOptions={!edit && !fromTheme ? themeOptions : null}
            onPickTheme={handlePickTheme}
            isEdit={Boolean(edit)}
          />
          
          {/* Action buttons */}
          <Box sx={{ display: "flex", gap: "20px", marginTop: "20px" }}>
            <Button
              sx={{ ...classes.buttonStyle }}
              variant="contained"
              onClick={goBack}
              startIcon={<ArrowLeftIcon />}
            >
              Go back
            </Button>
            <Button
              sx={{ ...classes.buttonStyle }}
              variant="contained"
              onClick={createDanceHandler}
              disabled={!isEnabled}
            >
              {edit ? "Update Dance Track" : "Create Dance Track"}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default UploadDanceFile;
